import {
  Avatar,
  Button,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { getFile } from 'API';
import {
  IUserUpdates,
  NotifSubjectsType,
  setNotifReaded,
} from 'API/Home/userUpdates';
import { requestFile } from 'API/medicalReferrals';
import { ButtonLinkOrDownload } from 'Components';
import { OneOrTwoButtons } from 'Components/OneOrTwoButtons';
import { shortDateFormat } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { NewLabelBig, NewLabelIcon, UploadPrescription } from 'Images';
import { MedicalReferralListMobile } from 'Modules/Home/UserUpdates/Row/MedicalReferralListMobile';
import { downloadFile } from 'Utils';
import {
  getUpdateGeneralDataByTypeId,
  shouldHaveTwoLinks,
  shouldPopup,
} from 'Utils/updates';
import { format } from 'date-fns';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface IRow {
  data: IUserUpdates;
  setShowDialog?: () => void;
}

export const Row = ({ data, setShowDialog }: IRow) => {
  const [isRead, setIsRead] = useState<Boolean>(data.readedDate ? true : false);

  const dictionary = useDictionary(Dictionary, DICTIONARY.Home).userUpdates;
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const jsonData = data.entityJSON && JSON.parse(data.entityJSON);
  const applyNumber = jsonData?.Items[0].ApplyNumber;
  const prescCode = jsonData?.Items[0].PrescCode;
  const requestNumber = jsonData?.Items[0].RequestNumber;
  const shouldShowPopup = shouldPopup(jsonData, data.notifEntityId);
  const { label, url, iconImg, secondLinkLabel, secondLinkUrl } =
    getUpdateGeneralDataByTypeId(
      data.notifEntityId,
      applyNumber,
      data.notifSubjectId,
      prescCode,
      requestNumber
    );
  const isTwoLinks = shouldHaveTwoLinks(data.notifSubjectId);

  const download = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setIsRead(true);
    await setNotifReaded(data.id);
    e.stopPropagation();
    let file;
    if (data.notifEntityId === NotifSubjectsType.MedicalReferrals) {
      file = await requestFile(jsonData.Items[0].Id);
    } else if (data.notifEntityId === NotifSubjectsType.Documents) {
      file = await getFile(jsonData.Items[0].Id);
    }
    if (file && file.fileContentBase64) {
      downloadFile(
        file.fileContentBase64,
        'pdf',
        file.documentName || 'New Document'
      );
    }
  };

  const handleNavigate = async () => {
    navigate(secondLinkUrl || url);
    setIsRead(true);
    await setNotifReaded(data.id);
  };

  const isDownload =
    jsonData?.Title === null &&
    jsonData?.Items[0].Id &&
    (data.notifEntityId === NotifSubjectsType.MedicalReferrals ||
      data.notifEntityId === NotifSubjectsType.Documents);

  return isDesktop ? (
    <Grid
      container
      sx={{
        borderBottom: '1px solid',
        borderColor: 'primary.shade_100',
        p: '24px',
        bgcolor: '#fff',
        borderRadius: '16px',
        alignItems: 'center',
      }}
    >
      <Grid item xs={12} md={0.5} alignSelf={'flex-start'}>
        <Stack
          sx={{
            width: '40px',
            p: '0',
            right: '6px',
            position: 'relative',
          }}
        >
          <Avatar src={iconImg} alt='' sx={{ width: '40px', height: '40px' }} />
        </Stack>
      </Grid>
      <Grid item xs={12} md={4.5} pl={1}>
        <Stack flexDirection={'row'}>
          <Typography variant='body1' component='h3'>
            {data.title}
          </Typography>
          {!isRead && (
            <Avatar
              src={NewLabelBig}
              alt=''
              sx={{
                width: '88px',
                height: '40px',
                position: 'absolute',
                right: '-16px',
                top: '12px',
              }}
            />
          )}
        </Stack>
        <Typography
          variant='body6'
          sx={{ mb: isDesktop ? undefined : '22px', minHeight: '40px' }}
        >
          {data.description}
        </Typography>

        {isDesktop && shouldShowPopup && (
          <Stack alignItems={'start'}>
            <Button
              sx={{
                fontSize: '16px',
                color: 'primary.main',
                fontWeight: 600,
                p: 0,
              }}
              onClick={() => setShowDialog && setShowDialog()}
            >
              {dictionary.toDownloadMedicalReferrals}
            </Button>
          </Stack>
        )}
      </Grid>
      <Grid item xs={12} md={0.5}>
        <Divider
          flexItem
          orientation='vertical'
          aria-hidden='true'
          sx={{
            width: '50%',
            height: '72px',
            borderColor: 'primary.shade_100',
          }}
        />
      </Grid>
      <Grid item xs={12} md={1.5}>
        <Stack
          sx={
            isDesktop
              ? { m: '0 32px' }
              : { flexDirection: 'row', m: '12px 0', alignItems: 'center' }
          }
        >
          <Typography
            variant='body3'
            sx={{ mr: isDesktop ? undefined : '6px' }}
          >
            {Dictionary[DICTIONARY.Updates].date}
          </Typography>
          <Typography variant='body5'>
            {format(new Date(data.createDate), shortDateFormat)}
          </Typography>
        </Stack>
      </Grid>
      <Grid item xs={12} md={5}>
        {isTwoLinks ? (
          <OneOrTwoButtons
            width={isTwoLinks ? '100%' : 'fit-content'}
            leftButton={{
              label: label,
              onClick: () => handleNavigate,
            }}
            rightButton={
              secondLinkLabel
                ? {
                    label: secondLinkLabel,
                    onClick: () => handleNavigate,
                    icon: UploadPrescription,
                  }
                : undefined
            }
          />
        ) : (
          <ButtonLinkOrDownload
            label={label}
            url={url}
            downloadFile={isDownload && download}
            isDownload={isDownload}
            isCallbackNavigation
            callbackNavigation={handleNavigate}
            isWidthBtn
          />
        )}
      </Grid>
    </Grid>
  ) : (
    <>
      <Stack
        bgcolor={'white'}
        border={'1px solid'}
        borderColor={'primary.shade_200'}
        borderRadius={'16px'}
        maxWidth={'388px'}
        justifyContent={'space-between'}
        pt={'20px'}
        position={'relative'}
      >
        <Stack flexDirection={'row'} gap={'16px'} p={'0 24px'}>
          <Avatar src={iconImg} alt='' sx={{ width: '28px', height: '28px' }} />
          <Stack gap={'8px'}>
            {!isRead && (
              <Avatar
                src={NewLabelIcon}
                alt=''
                sx={{
                  width: '42px',
                  height: '26px',
                  position: 'absolute',
                  right: '-8px',
                  top: '8px',
                }}
              />
            )}
            <Typography variant='body1'>{data.title}</Typography>
            <Typography variant='body6'>{data.description}</Typography>
            {shouldShowPopup && (
              <MedicalReferralListMobile items={jsonData?.Items} />
            )}
          </Stack>
        </Stack>

        {isTwoLinks ? (
          <OneOrTwoButtons
            width={isTwoLinks ? '100%' : 'fit-content'}
            leftButton={{
              label: label,
              onClick: () => handleNavigate,
            }}
            rightButton={
              secondLinkLabel
                ? {
                    label: secondLinkLabel,
                    onClick: () => handleNavigate,
                    icon: UploadPrescription,
                  }
                : undefined
            }
            isMobileVersion
          />
        ) : (
          <ButtonLinkOrDownload
            label={label}
            url={url}
            downloadFile={isDownload && download}
            isDownload={isDownload}
            isCallbackNavigation
            callbackNavigation={handleNavigate}
            isMobileVersion
          />
        )}
      </Stack>
    </>
  );
};
